import jwt from "jwt-decode";
import { clearAfterExpiry, clearKeepingDevice } from "../../../helpers/karkhanaEnabled";
import { clearStudioWorkspaceCaches } from "../../../helpers/studioSignupSession";

/**
 * Supervisor PIN sign-in. The phone keeps a key the server gave at sign-in (hkp.karkhana.phoneKey);
 * every time the app opens it swaps that key for a fresh login token, so the supervisor types the PIN
 * once and stays signed in until they log out or the studio admin turns access off or sets a new PIN.
 */

export const PHONE_KEY = "hkp.karkhana.phoneKey";

const base = process.env.REACT_APP_SERVERURL || "";

const read = (k: string): string | null => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};

export const hasRememberedPhone = (): boolean => !!read(PHONE_KEY);

const post = async (path: string, body: Record<string, unknown>): Promise<any> => {
  const res = await fetch(`${base}/karkhana/supervisor-login/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return res.json().catch(() => ({ success: false, status: res.status }));
};

const keepToken = (token: string) => {
  localStorage.setItem("authUser", JSON.stringify(jwt(token)));
  localStorage.setItem("token", token);
  localStorage.removeItem("refreshToken");
};

/** Phone + PIN. On success the phone is remembered; otherwise returns the server's message and status. */
export const signInWithPin = async (phone: string, pin: string): Promise<{ ok: true } | { ok: false; status?: number; message?: string }> => {
  const res = await post("pin", { phone, pin, device: navigator.userAgent });
  if (!res?.success || !res.data?.token) return { ok: false, status: res?.status, message: res?.message };
  // A key left from before (renew failed offline) is replaced: sign it out so it doesn't count as another phone.
  const old = read(PHONE_KEY);
  if (old && old !== res.data.phoneKey) post("logout", { phoneKey: old }).catch(() => undefined);
  clearStudioWorkspaceCaches();
  keepToken(res.data.token);
  localStorage.setItem(PHONE_KEY, res.data.phoneKey);
  return { ok: true };
};

/** What the supervisor reads when PIN sign-in fails; server codes never show raw. */
export const signInError = (status?: number, message?: string): string => {
  if (status === 401 || !message) return "Mobile number or PIN is wrong";
  if (message === "MODULE_REQUIRED" || message === "FEATURE_DISABLED") return "Karkhana is off for your studio. Ask the studio admin.";
  if (/^[A-Z_]+$/.test(message)) return "Could not sign in. Try again, or ask the studio admin.";
  return message;
};

/** Fresh token from the remembered phone. False (and the phone forgotten) when the studio signed it out. */
export const renewFromPhone = async (): Promise<boolean> => {
  const key = read(PHONE_KEY);
  if (!key) return false;
  try {
    const res = await post("renew", { phoneKey: key });
    if (res?.success && res.data?.token) {
      keepToken(res.data.token);
      return true;
    }
    if (res?.status === 401) localStorage.removeItem(PHONE_KEY);
    return false;
  } catch {
    return false; // offline: keep the key and try next time
  }
};

let renewing: Promise<boolean> | null = null;

/** The one renew every caller shares (Karkhana frame, expired-login recovery): never two at once. */
export const renewOnce = (): Promise<boolean> => {
  if (!renewing) {
    renewing = renewFromPhone().finally(() => {
      renewing = null;
    });
  }
  return renewing;
};

const RELOADED_AT = "hkp.karkhana.renewReloadAt";

/** After a renew, reload at most once every 30 s, so a server that keeps refusing can't loop the page. */
export const mayReloadAfterRenew = (now = Date.now()): boolean => {
  try {
    const last = Number(sessionStorage.getItem(RELOADED_AT) || 0);
    if (now - last < 30000) return false;
    sessionStorage.setItem(RELOADED_AT, String(now));
    return true;
  } catch {
    return false;
  }
};

/** Renew when the current token is gone or ends within `withinMs` (two days; 0 = already expired). */
export const tokenNeedsRenewing = (now = Date.now(), withinMs = 2 * 24 * 60 * 60 * 1000): boolean => {
  const token = read("token");
  if (!token) return true;
  try {
    const exp = (jwt(token) as { exp?: number }).exp;
    return !exp || exp * 1000 - now < withinMs;
  } catch {
    return true;
  }
};

/**
 * Expired-login recovery for the app shell. Only a supervisor's own remembered phone renews (never an
 * owner who signed in on that phone). "reloaded" / "renewed" (reload held back to avoid a loop) /
 * "signed-out" (studio signed the phone out: back to the PIN, language and mobile kept) / "offline"
 * (key kept, try again later) / "not-mine" (fall back to the normal logout).
 */
export const recoverExpiredLogin = async (
  isSupervisor: boolean,
): Promise<"reloaded" | "renewed" | "signed-out" | "offline" | "not-mine"> => {
  if (!isSupervisor || !hasRememberedPhone()) return "not-mine";
  if (await renewOnce()) {
    if (!mayReloadAfterRenew()) return "renewed";
    window.location.reload();
    return "reloaded";
  }
  if (hasRememberedPhone()) return "offline";
  // Expired, not a logout: unsaved Daily work stays for the same supervisor's next sign-in.
  clearAfterExpiry(true);
  return "signed-out";
};

/** A password sign-in (studio or supervisor page) is someone else's session: a phone key left here is dropped. */
export const dropRememberedPhone = () => {
  if (hasRememberedPhone()) void forgetPhone();
};

/** Logout from anywhere (Karkhana More, the studio header): forget the phone on the server, keep language and mobile. */
export const logoutThisPhone = () => {
  void forgetPhone();
  clearKeepingDevice();
};

/** Logout forgets this phone on the server too; the PIN is needed next time. */
export const forgetPhone = async (): Promise<void> => {
  const key = read(PHONE_KEY);
  try {
    localStorage.removeItem(PHONE_KEY);
  } catch {
    // Storage blocked: nothing kept anyway.
  }
  if (key) await post("logout", { phoneKey: key }).catch(() => undefined);
};
